import AddCardIcon from '@mui/icons-material/AddCard';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import Alert from '@mui/material/Alert';
import { Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { FakeButtonLink } from '../../../../components/FakeLink';
import { Field, FieldGrid, SectionCard } from '../../../../components/Section';
import COLORS from '../../../../constants/colors';
import { isProduction } from '../../../../constants/environment';
import { usePermissions } from '../../../../hooks';
import { useFetchTreasuryAnalysisQuery } from '../../../../redux/features/treasury/treasury.apiSlice';
import { locations } from '../../../../routes/locations';
import { TreasuryAnalysis } from '../../../../types/treasury';
import { round2Format } from '../../../../utils/number';
import * as permissions from '../../../../utils/permissions';
import { getTreasuryErrorMessage } from '../../../../utils/treasury';
import { TreasuryTestTopUpDialog } from './TreasuryTestTopUpDialog';

type TreasuryPortfolioSummaryProps = {
  companyId: number;
};

const sar = (value: number | null | undefined) => `SAR ${round2Format(Number(value) || 0)}`;

/**
 * The treasury at a glance, all live: the treasury portfolio's value and return straight from madkhol-portfolio (the
 * same calculation as the app's portfolio card) and the treasury bank's cash. One portfolio, so one summary card.
 */
export const TreasuryPortfolioSummary = ({ companyId }: TreasuryPortfolioSummaryProps) => {
  // A write that moves the bank (execute, redeem, withdraw) refetches this too.
  const {
    data,
    isFetching: isLoading,
    isError,
    error: loadError,
    refetch: load,
  } = useFetchTreasuryAnalysisQuery(companyId);
  // A failed read shows the error, never the last numbers.
  const analysis: TreasuryAnalysis | null = isError ? null : data ?? null;
  const error = isError ? getTreasuryErrorMessage(loadError, 'Failed to load the treasury numbers') : null;
  // The dummy statement routes are gated on `payment-statement` read in madkhol-admin.
  const canTestTopUp = usePermissions({ items: [permissions.PaymentPermission.paymentStatement] });
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);

  const portfolio = analysis?.portfolio ?? null;
  const gain = portfolio?.returnSAR ?? 0;
  const gainColor = gain < 0 ? COLORS.RED : COLORS.SECONDARY_GREEN;
  const gainSign = gain > 0 ? '+' : '';

  // Loading / error, shared by both cards; null once the numbers are in.
  const renderState = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (error || !analysis) {
      return <Alert severity="error">{error ?? 'Failed to load the treasury numbers'}</Alert>;
    }

    return null;
  };

  const renderBank = () => {
    const state = renderState();

    if (state || !analysis) {
      return state;
    }

    const bank = analysis.treasuryBank;

    // An admin service built before the bank was added answers without it — say so instead of crashing.
    if (!bank) {
      return (
        <Alert severity="warning">
          The admin service did not return the treasury bank — restart madkhol-admin to pick up the latest build.
        </Alert>
      );
    }

    return (
      <FieldGrid columns={4}>
        <Field label="Available">
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>{sar(bank.amount)}</Typography>
        </Field>
        <Field label="On Hold (withdrawals)" value={sar(bank.hold)} />
        <Field label="Total" value={sar(Number(bank.amount) + Number(bank.hold))} />
        <Field label="IBAN" value={bank.iban ?? '—'} />
      </FieldGrid>
    );
  };

  const renderPortfolio = () => {
    const state = renderState();

    if (state || !analysis) {
      return state;
    }

    return (
      <FieldGrid columns={4}>
        <Field label="Portfolio Value">
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
            {portfolio ? sar(portfolio.overallBalance) : '—'}
          </Typography>
        </Field>
        <Field label="Gain / Loss">
          {portfolio ? (
            <>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: gainColor }}>
                {gainSign}
                {sar(gain)}
              </Typography>
              <Typography variant="caption" sx={{ color: gainColor }}>
                {gainSign}
                {round2Format(portfolio.returnPercentage)}%
              </Typography>
            </>
          ) : (
            '—'
          )}
        </Field>
        <Field label="Invested" value={portfolio ? sar(portfolio.totalInvestedAmount) : '—'} />
        {portfolio && <Field label="Cost Base" value={sar(portfolio.totalCostBase)} />}
        {portfolio && <Field label="Portfolio Cash" value={sar(portfolio.cacheAllocationValue)} />}
      </FieldGrid>
    );
  };

  return (
    <Stack spacing={2}>
      <SectionCard
        title="Treasury Bank"
        description={
          analysis?.treasuryBank?.accountNumber
            ? `Bank #${analysis.treasuryBank.id} · account ${analysis.treasuryBank.accountNumber}`
            : undefined
        }
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            {!isProduction && canTestTopUp && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<AddCardIcon />}
                onClick={() => setIsTopUpOpen(true)}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                Test Top-up
              </Button>
            )}
            <Button size="small" startIcon={<RefreshIcon />} onClick={load}>
              Refresh
            </Button>
          </Stack>
        }
      >
        {renderBank()}
      </SectionCard>

      <SectionCard
        title={portfolio ? `Treasury Portfolio — ${portfolio.nameEn}` : 'Treasury Portfolio'}
        description={
          !isLoading && analysis && !portfolio
            ? 'No treasury portfolio yet — the first executed contribution creates it.'
            : undefined
        }
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            {portfolio?.currentStatus && <Chip size="small" color="info" label={`Order ${portfolio.currentStatus}`} />}
            {portfolio?.status && <Chip size="small" label={portfolio.status} />}
            {portfolio && (
              <FakeButtonLink startIcon={<OpenInNewIcon />} href={locations.subscribedPortfolio(portfolio.id)}>
                Portfolio #{portfolio.id}
              </FakeButtonLink>
            )}
            <Button size="small" startIcon={<RefreshIcon />} onClick={load}>
              Refresh
            </Button>
          </Stack>
        }
      >
        {renderPortfolio()}
      </SectionCard>

      <TreasuryTestTopUpDialog
        open={isTopUpOpen}
        companyId={companyId}
        onClose={() => setIsTopUpOpen(false)}
        onDone={load}
      />
    </Stack>
  );
};
